import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'clsx';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex';
import { useFocusJail } from 'ui-components/hooks/use-focus-jail';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { DrawerProvider } from './context';
import style from './drawer.module.less';
import { CloseModalHotkey } from '../../../components/hotkeys/close-modal';
import { ConfirmDialogueModal } from '../../../components/modals/confirm-dialogue-modal';
import { useModal } from '../../../hooks/use-modal';
import { useModalManagerPortal } from '../../../portals/hooks';

/**
 * @typedef {import('./drawer-types.d.ts').DrawerType} DrawerType
 */

/**
 * @typedef {import('./drawer-types.d.ts').CancelHandler} CancelHandler
 */

/**
 * @typedef {import('./drawer-types.d.ts').CloseHandler} CloseHandler
 */

const DrawerContent = memo(({ drawerRef, onCancelCb, onClose, dataTestId, className, canvasClassName, children }) => (
  <>
    <div className={style['drawer-background']} data-testid={`${dataTestId}-background`} onClick={onCancelCb} />
    <DrawerProvider onCancel={onCancelCb} onClose={onClose}>
      <div ref={drawerRef} tabIndex={-1} className={classnames(style.drawer, className)} data-testid={dataTestId}>
        <FlexColumn className={classnames(canvasClassName, style.canvas)}>{children}</FlexColumn>
      </div>
    </DrawerProvider>
    <CloseModalHotkey onClose={onCancelCb} />
  </>
));

/**
 * @type {DrawerType}
 */
const Drawer = ({
  canvasClassName,
  className,
  onCancel,
  onClose,
  children,
  confirmCancelBodyMessage,
  confirmCancelButtonMessage,
  confirmCancelHeaderMessage,
  shouldNotUsePortal = false,
  dataTestId = 'drawer',
}) => {
  const zIndex = useMaxZ();
  const zIndexStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalManagerPortalRef = useModalManagerPortal();

  // Stable ref for the focus jail — identity must not change null→element or
  // useFocusJail tears down and re-runs after a child input has focused,
  // stealing focus back. The Drawer portals its content and returns null on the
  // first render, so the callback ref sets both the stable ref and a state value
  // that drives the re-render used to place initial focus once the div mounts.
  const drawerRef = useRef(null);
  const [drawerEl, setDrawerEl] = useState(null);
  const attachDrawer = useCallback((el) => {
    drawerRef.current = el;
    setDrawerEl(el);
  }, []);
  useFocusJail(drawerRef);
  useEffect(() => {
    if (drawerEl !== null && !drawerEl.contains(document.activeElement)) {
      drawerEl.focus();
    }
  }, [drawerEl]);

  const { open: onOpenConfirmModal, close: onCloseConfirmModal, isOpen: isConfirmModalOpen } = useModal();

  /**
   * @type {CancelHandler}
   */
  const onCancelCb = useCallback(() => {
    if (confirmCancelBodyMessage && confirmCancelButtonMessage && confirmCancelHeaderMessage) {
      onOpenConfirmModal();
    } else {
      onCancel();
    }
  }, [confirmCancelBodyMessage, confirmCancelButtonMessage, confirmCancelHeaderMessage, onCancel, onOpenConfirmModal]);

  const drawerContent = (
    <DrawerContent
      drawerRef={attachDrawer}
      onCancelCb={onCancelCb}
      onClose={onClose}
      dataTestId={dataTestId}
      className={className}
      canvasClassName={canvasClassName}
    >
      {children}
    </DrawerContent>
  );

  const jsxToRender = (
    <div className={style.container} style={zIndexStyle}>
      {drawerContent}
      {isConfirmModalOpen ? (
        <ConfirmDialogueModal
          body={<FormattedMessage {...confirmCancelBodyMessage} />}
          buttonMessage={<FormattedMessage {...confirmCancelButtonMessage} />}
          header={<FormattedMessage {...confirmCancelHeaderMessage} />}
          onClose={onCloseConfirmModal}
          onConfirm={onCancel}
        />
      ) : null}
    </div>
  );

  if (shouldNotUsePortal) {
    return jsxToRender;
  }

  if (!modalManagerPortalRef) {
    return null;
  }

  return ReactDOM.createPortal(jsxToRender, modalManagerPortalRef);
};

/**
 * @deprecated Use `Drawer` from `@mantine/core` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export { Drawer };
